<script setup lang="ts">
// A bottom drawer: a resize bar over a body, dragged by pointer and expanded by
// the chevron on the bar. It keeps no state of its own — the fraction and the
// expanded flag belong to the page, because every page that mounts a drawer
// shares those two numbers with the map behind it and the rails above it.
//
// Nor does it place itself: the page supplies a class on the root that gives the
// drawer its position, background and border, and this component contributes
// only the height the fraction asks for.
import { computed, onBeforeUnmount, ref } from "vue";

interface Props {
	/** Height as a fraction (0–1) of the box the drawer sits in. */
	fraction: number;
	/** The page's "drawer is at the top" flag, shown as the chevron's
	 * direction. A drag to the top reports it; the drag never writes it, and
	 * the page maps the boolean onto its own fractions. */
	expanded?: boolean;
	/** A closed drawer keeps its bar and drops its body and its height. */
	open?: boolean;
}

interface Emits {
	(e: "update:fraction", fraction: number): void;
	(e: "update:expanded", expanded: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
	expanded: false,
	open: true,
});

const emit = defineEmits<Emits>();

// Map Review's established drawer bounds. The drag is clamped here, in the one
// widget every drawer shares, so no page can drag past the box it covers or
// under the bar it has to grab next.
const MIN_FRACTION = 0.15;
const MAX_FRACTION = 1;
// Releasing at the top is the expand gesture, and a drag that ends a pixel
// short of the top is still at the top.
const EXPANDED_FRACTION = 0.99;

// Pointer drag on the grab bar. `pointermove` is listened for on the window, so
// a fast drag that leaves the bar keeps resizing.
const dragging = ref(false);
let dragStartY = 0;
let dragStartFraction = 0;
let dragContainer = 0;
// What this drag has emitted, so the release test reads the drag's own number
// rather than waiting for the page to hand the prop back.
let draggedFraction = props.fraction;

function beginDrag(event: PointerEvent) {
	// The bar's parent is the root and the root's parent is the box the drawer
	// fills: a fraction is measured against that box, not against the drawer.
	const container = (event.currentTarget as HTMLElement).parentElement
		?.parentElement;
	if (!container) return;
	dragContainer = container.clientHeight;
	if (dragContainer <= 0) return;
	dragging.value = true;
	dragStartY = event.clientY;
	dragStartFraction = props.fraction;
	draggedFraction = props.fraction;
	(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
	window.addEventListener("pointermove", onDragMove);
	window.addEventListener("pointerup", endDrag);
}

function onDragMove(event: PointerEvent) {
	if (!dragging.value) return;
	const delta = (dragStartY - event.clientY) / dragContainer;
	// Every move is written straight through, so whatever the page hangs off
	// this fraction follows the drag live.
	draggedFraction = Math.min(
		MAX_FRACTION,
		Math.max(MIN_FRACTION, dragStartFraction + delta),
	);
	emit("update:fraction", draggedFraction);
}

function endDrag() {
	dragging.value = false;
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", endDrag);
	// Dragging to the top is the same gesture as expanding. Dragging away from
	// it needs no write: a page that derives "expanded" from the fraction sees
	// it fall as soon as the drawer leaves the top.
	if (draggedFraction >= EXPANDED_FRACTION) emit("update:expanded", true);
}

// A drag still in flight when the drawer unmounts would otherwise leave its
// window listeners attached to nothing.
onBeforeUnmount(() => {
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", endDrag);
});

// An open drawer states its own height and nothing else. Closed, it states no
// height at all, so the page's sizing decides where the bar sits.
const rootStyle = computed(() =>
	props.open ? { height: `${props.fraction * 100}%` } : undefined,
);
</script>

<template>
	<div class="bottom-drawer" :style="rootStyle">
		<!-- The bar is the drawer's whole chrome: `grab` takes the room up to the
		     chevron (a table switcher, a title, whatever the page reads from)
		     and `grab-end` takes the controls after it. The bar itself is the
		     drag target, so any pointer on the empty stretch resizes. -->
		<div
			class="bottom-drawer-grab"
			:class="{ dragging }"
			role="separator"
			aria-label="Resize drawer"
			aria-orientation="horizontal"
			@pointerdown.prevent="beginDrag"
		>
			<div class="bottom-drawer-grab-slot">
				<slot name="grab" />
			</div>
			<button
				type="button"
				class="bottom-drawer-btn"
				:aria-label="expanded ? 'Shrink drawer' : 'Expand drawer'"
				:title="expanded ? 'Shrink drawer' : 'Expand drawer'"
				@pointerdown.stop
				@click="emit('update:expanded', !expanded)"
			>
				<i
					class="bi"
					:class="expanded ? 'bi-chevron-down' : 'bi-chevron-up'"
				></i>
			</button>
			<slot name="grab-end" />
		</div>

		<div v-if="open" class="bottom-drawer-body">
			<slot />
		</div>
	</div>
</template>

<style scoped>
/* No position and no dressing: the page's class on this root places and paints
   the drawer. What is here is the flex column that puts the body under the bar,
   the bar's own chrome, and the body's share of the height. */
.bottom-drawer {
	display: flex;
	flex-direction: column;
}

.bottom-drawer-grab {
	position: relative;
	display: flex;
	align-items: center;
	gap: 4px;
	height: 28px;
	flex: 0 0 28px;
	padding: 0 2px 0 6px;
	cursor: ns-resize;
	background: #f1f3f5;
	border-bottom: 1px solid #dee2e6;
	touch-action: none;
}

.bottom-drawer-grab.dragging {
	background: #e9ecef;
}

/* The `grab` slot's box: it takes its content's width and the bar's remainder
   goes to its right-hand margin, so the chevron and `grab-end` sit at the far
   end however much the page puts on the left. A row, so a page's title and its
   hint sit side by side and both can truncate. */
.bottom-drawer-grab-slot {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-right: auto;
	min-width: 0;
	overflow: hidden;
}

/* One rule for both bar buttons — the built-in chevron and the close control a
   page hands to `grab-end`. `:deep` spans the slot boundary, so the page's
   button wears the drawer's chrome instead of restating it. */
.bottom-drawer-grab :deep(.bottom-drawer-btn) {
	border: 0;
	background: transparent;
	color: #495057;
	padding: 0 8px;
	height: 100%;
	font-size: 0.75rem;
	line-height: 1;
}

.bottom-drawer-grab :deep(.bottom-drawer-btn:hover) {
	color: #0d6efd;
}

.bottom-drawer-body {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
}
</style>
